import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionTag, SectionTitle } from "./shared";
import { X } from "lucide-react";


// Open your file and modify the submitLead function:
export async function submitLead(payload) {
  // FIXED: Changed the endpoint path from the base domain to the correct API submission endpoint
  const res = await axios.post("https://api.web3forms.com/submit", {
    access_key: "64909ee7-ab9a-4271-a738-0d8c87ec8e69", 
    subject: `New Lead from Radiance Realtors: ${payload.name}`,
    ...payload 
  });
  return res.data;
}


const BUDGETS = ["Under Rs 60 L", "Rs 60 L - 1 Cr", "Rs 1 - 2.5 Cr", "Rs 2.5 Cr+"];
const TYPES = ["Apartment", "Plot", "Villa", "Commercial", "Studio", "Resale"];
const PURPOSES = ["End Use", "Investment", "Both"];
const TIMELINES = ["Immediately", "1 - 3 Months", "3 - 6 Months", "Just Exploring"];
// ... leave everything else down below exactly the same


const INITIAL = { name: "", phone: "", whatsapp: "", budget: "", property_type: "", purpose: "", configuration: "", timeline: "", message: "" };

export function LeadDialog({ open, onClose, leadType, title, context }) {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setBusy(true);
    try {
      await submitLead({ ...form, lead_type: leadType, message: context ? `${context}${form.message ? " - " + form.message : ""}` : form.message });
      toast.success("Thank you. A Radiance advisor will reach out shortly.");
      setForm(INITIAL);
      onClose();
    } catch {
      toast.error(".");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lead-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lead-dialog-title"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.form
            onSubmit={submit}
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-lg bg-[#111111] border border-[#D4AF37]/30 rounded-[2px] p-7 max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Radiance Realtors</p>
                <h3 id="lead-dialog-title" className="font-serif-lux text-2xl text-[#F5F5F0]">{title}</h3>
                {context && <p className="text-[12px] text-[#71717A] mt-1">{context}</p>}
              </div>
              <button type="button" data-testid="lead-dialog-close" onClick={onClose} className="p-2 border border-[#D4AF37]/30 rounded-full text-[#F3E5AB]"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="lead-name-input" className="lux-input" placeholder="Full Name *" value={form.name} onChange={set("name")} />
              <input data-testid="lead-phone-input" className="lux-input" placeholder="Phone *" value={form.phone} onChange={set("phone")} />
              <input data-testid="lead-whatsapp-input" className="lux-input" placeholder="WhatsApp Number" value={form.whatsapp} onChange={set("whatsapp")} />
              <select data-testid="lead-budget-select" className="lux-input" value={form.budget} onChange={set("budget")}>
                <option value="">Budget</option>
                {BUDGETS.map((b) => <option key={b}>{b}</option>)}
              </select>
              <select data-testid="lead-type-select" className="lux-input" value={form.property_type} onChange={set("property_type")}>
                <option value="">Property Type</option>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <select data-testid="lead-purpose-select" className="lux-input" value={form.purpose} onChange={set("purpose")}>
                <option value="">Purpose</option>
                {PURPOSES.map((p) => <option key={p}>{p}</option>)}
              </select>
              <input data-testid="lead-config-input" className="lux-input" placeholder="Preferred Configuration (e.g. 3 BHK)" value={form.configuration} onChange={set("configuration")} />
              <select data-testid="lead-timeline-select" className="lux-input" value={form.timeline} onChange={set("timeline")}>
                <option value="">Timeline</option>
                {TIMELINES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <textarea data-testid="lead-message-input" className="lux-input col-span-2" rows={2} placeholder="Anything specific you're looking for?" value={form.message} onChange={set("message")} />
            </div>
            <button
              data-testid="lead-dialog-submit"
              disabled={busy}
              className="mt-5 w-full py-4 text-[12px] font-semibold uppercase tracking-[0.22em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black hover:from-[#D4AF37] hover:to-[#F3E5AB] transition-colors rounded-[2px] disabled:opacity-50"
            >
              {busy ? "Sending..." : "Submit Request"}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function LeadCapture({ onInventory }) {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setBusy(true);
    try {
      await submitLead({ ...form, lead_type: "find-property" });
      toast.success("Request received. An advisor will curate matching GYC inventory for you.");
      setForm(INITIAL);
    } catch {
      toast.error("Could not submit right now. Please try WhatsApp or call us.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-40 bg-[#0D0D0D]" data-testid="lead-capture-section">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <SectionTag center>Begin Here</SectionTag>
        <SectionTitle className="text-center mb-4">Tell Us What <span className="text-gold-gradient italic">You&rsquo;re Looking For.</span></SectionTitle>
        <Reveal delay={0.12}>
          <p className="text-center text-sm text-[#71717A] mb-12 max-w-lg mx-auto">
            A short brief is enough. Our desk responds with matched inventory, honest pricing context and next steps.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="border border-[#D4AF37]/20 bg-[#0A0A0A] rounded-[2px] p-7 lg:p-10" data-testid="lead-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="form-name-input" className="lux-input" placeholder="Full Name *" value={form.name} onChange={set("name")} />
              <input data-testid="form-phone-input" className="lux-input" placeholder="Phone *" value={form.phone} onChange={set("phone")} />
              <input data-testid="form-whatsapp-input" className="lux-input" placeholder="WhatsApp Number" value={form.whatsapp} onChange={set("whatsapp")} />
              <select data-testid="form-budget-select" className="lux-input" value={form.budget} onChange={set("budget")}>
                <option value="">Budget</option>
                {BUDGETS.map((b) => <option key={b}>{b}</option>)}
              </select>
              <select data-testid="form-type-select" className="lux-input" value={form.property_type} onChange={set("property_type")}>
                <option value="">Property Type</option>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <select data-testid="form-purpose-select" className="lux-input" value={form.purpose} onChange={set("purpose")}>
                <option value="">Purpose</option>
                {PURPOSES.map((p) => <option key={p}>{p}</option>)}
              </select>
              <input data-testid="form-config-input" className="lux-input" placeholder="Preferred Configuration" value={form.configuration} onChange={set("configuration")} />
              <select data-testid="form-timeline-select" className="lux-input" value={form.timeline} onChange={set("timeline")}>
                <option value="">Timeline</option>
                {TIMELINES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <button
                data-testid="form-submit-btn"
                disabled={busy}
                className="flex-1 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black hover:from-[#D4AF37] hover:to-[#F3E5AB] transition-colors rounded-[2px] disabled:opacity-50"
              >
                {busy ? "Sending..." : "Find My Property"}
              </button>
              <button
                type="button"
                data-testid="form-inventory-btn"
                onClick={onInventory}
                className="flex-1 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 transition-colors rounded-[2px]"
              >
                Get Today&rsquo;s Inventory
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
